import React from 'react'
import { NodeIdMap } from '../../version_mapping/utility'
import { NodePath } from '../utility/NodePath'
import {
  getAncestorOfType,
  getLessonPlan,
  getQuestionNodes,
  getUnit,
  getVerifiedQuestionCounts,
} from './utility'
import { Box, Button } from '@mui/material'
import { INode } from 'shared/types/tree'
import { NodeType } from 'shared/types/ingestion'
import { getNodeUrl, openNode } from '../../utility/TreeNavigation'
import { Checkmark } from '../../../ingestion/IMSpanish/components/Checkmark'
import { useGetVerifiedQuestionCount } from './hooks'
import { nodePath } from '../utility/utility'

export function titleCell(idMap?: NodeIdMap) {
  return (nodeId: string) => {
    if (!idMap) return null
    const { node } = idMap[nodeId]
    return node?.title ?? null
  }
}

export function NodeNavButton({
  buttonData,
}: {
  buttonData: {
    nodeID: INode
    nodeIDMap: NodeIdMap
    buttonTitle: string
  }[]
}) {
  return (
    <>
      {buttonData.map((button) => (
        <Box key={button.buttonTitle}>
          <Button
            onClick={() => {
              openNode(button.nodeID, button.nodeIDMap)
            }}
          >
            {button.buttonTitle}
          </Button>
        </Box>
      ))}
    </>
  )
}

export function nodePathCell(idMap?: NodeIdMap) {
  return (nodeId: string) => {
    if (!idMap) return null
    if (!(nodeId in idMap)) return null
    const { node } = idMap[nodeId]
    return <NodePath node={node} idMap={idMap} />
  }
}

export function cellForType(type: NodeType, idMap?: NodeIdMap) {
  return (nodeId: string) => {
    if (!idMap) return null
    if (!(nodeId in idMap)) return null
    const { node } = idMap[nodeId]
    const ancestor = getAncestorOfType(type, node, idMap)
    if (!ancestor) return 'Didnt Find'
    return ancestor.title
  }
}

// Using requiredTitle & optionalTitle as variable name due
// ordering in the cell not being consistant
export function DiffCellText({
  requiredTitle,
  optionalTitle,
}: {
  requiredTitle: string
  optionalTitle?: string
}) {
  return (
    <Box>
      <Box>{requiredTitle}</Box>
      {optionalTitle && optionalTitle !== requiredTitle && (
        <Box>{optionalTitle}</Box>
      )}
    </Box>
  )
}

export function titleDiffCell({
  destIdMap,
  nodeMapping,
}: {
  destIdMap?: NodeIdMap
  nodeMapping: Record<string, INode[]>
}) {
  return (destinationNodeId: string) => {
    if (!destIdMap) return null
    if (!(destinationNodeId in destIdMap)) return null
    const {
      node: { title },
    } = destIdMap[destinationNodeId]
    const sourceTitle = nodeMapping[destinationNodeId][0].title
    return <DiffCellText requiredTitle={sourceTitle} optionalTitle={title} />
  }
}

export function unitDiffCell({
  destIdMap,
  sourceIdMap,
  nodeMapping,
}: {
  destIdMap?: NodeIdMap
  sourceIdMap?: NodeIdMap
  nodeMapping: Record<string, INode[]>
}) {
  return (destinationNodeId: string) => {
    if (!destIdMap) return null
    if (!(destinationNodeId in destIdMap)) return null
    const { node } = destIdMap[destinationNodeId]
    const destinationUnit = getUnit(node, destIdMap)
    if (!destinationUnit) return 'Didnt Find Unit'
    const sourceUnit =
      sourceIdMap && getUnit(nodeMapping[destinationNodeId][0], sourceIdMap)
    return (
      <DiffCellText
        requiredTitle={destinationUnit.title}
        optionalTitle={sourceUnit?.title}
      />
    )
  }
}

export function questionDiffCell({
  destIdMap,
  nodeMapping,
}: {
  destIdMap?: NodeIdMap
  nodeMapping: Record<string, INode[]>
}) {
  return (destinationNodeId: string) => {
    if (!destIdMap) return null
    if (!(destinationNodeId in destIdMap)) return null
    const { node } = destIdMap[destinationNodeId]
    return [
      getQuestionNodes(node).length,
      getQuestionNodes(nodeMapping[destinationNodeId][0]).length,
    ].join(', ')
  }
}

export function VerifyCell({
  destinationNodeId,
}: {
  destinationNodeId: string
}) {
  const getVerifiedCount = useGetVerifiedQuestionCount()
  const verifyCount = getVerifiedCount(destinationNodeId)
  if (!verifyCount) return null
  return <Checkmark />
}

export function lessonPlanDiffCell({
  destIdMap,
  sourceIdMap,
  nodeMapping,
}: {
  destIdMap?: NodeIdMap
  sourceIdMap?: NodeIdMap
  nodeMapping: Record<string, INode[]>
}) {
  return (destinationNodeId: string) => {
    if (!destIdMap) return null
    if (!(destinationNodeId in destIdMap)) return null
    const { node } = destIdMap[destinationNodeId]
    const lessonPlan = getLessonPlan(node, destIdMap)
    if (!lessonPlan) return 'Didnt Find Lesson Plan'
    const sourceLessonPlan =
      sourceIdMap &&
      getLessonPlan(nodeMapping[destinationNodeId][0], sourceIdMap)
    return (
      <DiffCellText
        requiredTitle={lessonPlan.title}
        optionalTitle={sourceLessonPlan?.title}
      />
    )
  }
}

export function openKiddomCell(idMap?: NodeIdMap) {
  return (nodeId: string) => {
    if (!idMap) return null
    if (!(nodeId in idMap)) return null
    const { node } = idMap[nodeId]
    const nodePath = [
      {
        nodeID: node,
        nodeIDMap: idMap,
        buttonTitle: 'Open',
      },
    ]

    return <NodeNavButton buttonData={nodePath} />
  }
}

export function openSourceDestCell({
  destIdMap,
  sourceIdMap,
  nodeMapping,
}: {
  destIdMap?: NodeIdMap
  sourceIdMap?: NodeIdMap
  nodeMapping: Record<string, INode[]>
}) {
  return (destinationNodeId: string) => {
    if (!destIdMap) return null
    if (!(destinationNodeId in destIdMap)) return null
    const { node } = destIdMap[destinationNodeId]
    const sourceNode = nodeMapping[destinationNodeId][0]
    const nodePaths = [
      {
        nodeID: node,
        nodeIDMap: destIdMap,
        buttonTitle: 'Dest',
      },
      {
        nodeID: sourceNode,
        nodeIDMap: sourceIdMap!,
        buttonTitle: 'Source',
      },
    ]
    return <NodeNavButton buttonData={nodePaths} />
  }
}

export function getNodeRowDataAsString({
  destIdMap,
  sourceIdMap,
  nodeMapping,
  dataType,
}: {
  destIdMap?: NodeIdMap
  sourceIdMap?: NodeIdMap
  nodeMapping?: Record<string, INode[]>
  dataType: string
}) {
  return (destinationNodeId: string) => {
    if (!destIdMap) return ''
    if (!(destinationNodeId in destIdMap)) return ''
    const { node } = destIdMap[destinationNodeId]
    switch (dataType) {
      case 'title': {
        let title = node.title
        if (!nodeMapping) {
          return title
        }
        const source = nodeMapping[destinationNodeId][0]
        if (source && title !== source.title) {
          title = 'Source: ' + source.title + ' | Destination: ' + title
        }
        return title
      }
      case 'qcount': {
        if (!nodeMapping) {
          return ''
        }
        const questionCount = [
          getQuestionNodes(node).length,
          getQuestionNodes(nodeMapping[destinationNodeId][0]).length,
        ].join(', ')
        return questionCount
      }
      case 'verify': {
        let verifyStatus = 'not verified'
        if (!nodeMapping) {
          return verifyStatus
        }
        const verifyCount = getVerifiedQuestionCounts(
          destinationNodeId,
          destIdMap,
          nodeMapping,
        )
        if (verifyCount) {
          verifyStatus = 'verified'
        }
        return verifyStatus
      }
      case 'unit': {
        const destinationUnit = getUnit(node, destIdMap)
        let unit = "Didn't Find Unit"
        if (destinationUnit) {
          unit = destinationUnit.title
          if (!nodeMapping) {
            return unit
          }
          const sourceUnit =
            sourceIdMap &&
            getUnit(nodeMapping[destinationNodeId][0], sourceIdMap)
          if (sourceUnit && sourceUnit.title !== unit) {
            unit =
              'Source: ' +
              sourceUnit.title +
              ' | Destination: ' +
              destinationUnit.title
          }
        }
        return unit
      }
      case 'lesson': {
        let lesson = "Didn't Find Lesson Plan"
        const destinationLessonPlan = getLessonPlan(node, destIdMap)
        if (destinationLessonPlan) {
          lesson = destinationLessonPlan.title
          if (!nodeMapping) {
            return lesson
          }
          const sourceLessonPlan =
            sourceIdMap &&
            getLessonPlan(nodeMapping[destinationNodeId][0], sourceIdMap)
          if (sourceLessonPlan && lesson !== sourceLessonPlan.title) {
            lesson =
              'Source: ' +
              sourceLessonPlan.title +
              ' | Destination: ' +
              destinationLessonPlan.title
          }
        }
        return lesson
      }
      case 'url': {
        if (!sourceIdMap || !nodeMapping) {
          return getNodeUrl(node, destIdMap)
        }
        const sourceNode = nodeMapping[destinationNodeId][0]
        const path =
          'Source: ' +
          getNodeUrl(sourceNode, sourceIdMap) +
          ' | Destination: ' +
          getNodeUrl(node, destIdMap)
        return path
      }
      case 'path': {
        const path = nodePath(node, destIdMap).map(({ type }) => type)
        return path.join(' -> ')
      }
    }

    return ''
  }
}
